import { useCallback, useMemo } from 'react';

import { getDateWithCache } from '@/utils/dates';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
import { getUserTimezone } from '@ui/calendar/utils';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { emptySpace } from '@/utils/empty-space';

type DateDescriptionProps = {
  dates: {
    date: Date | null;
    fullDate: string | null;
    month: string | null;
    day: string | null;
    endDate?: Date | null;
    timeZone?: Intl.DateTimeFormatOptions['timeZone'];
    localStartDate?: Date | null;
    localEndDate?: Date | null;
  } | null;
  displayTimezone?: boolean;
};

const DateDescription = ({ dates, displayTimezone }: DateDescriptionProps) => {
  const getRuntime = useCallback(() => {
    if (!dates?.date) return null;
    if (!dates?.endDate) return null;
    const start = getDateWithCache(dates.date.getTime());
    const end = dates.endDate ? getDateWithCache(dates.endDate.getTime()) : new Date();
    const diff = end.getTime() - start.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  }, [dates]);

  const timeZone = useMemo(() => {
    return dates?.timeZone || getUserTimezone();
  }, [dates]);

  if (!dates) return null;
  return (
    <TooltipPortal
      disabled={!displayTimezone}
      tooltipContent={
        <Text color="var(--gray-true-dark-100)" className="C1">
          {timeZone}
        </Text>
      }
      delayStart={500}
      animationType="slide-up"
    >
      <div>
        <Text
          className={`S3 ${dates?.fullDate ? '' : 'margin-b-6'}`}
          width={!dates?.date ? '180px' : undefined}
          maxWidth="100%"
          height={!dates?.date ? '16px' : undefined}
          backgroundColor={!dates.fullDate ? 'rgba(var(--gray-800-rgb), 0.7)' : undefined}
        >
          {dates?.fullDate || emptySpace}
        </Text>
        <Text
          className="C1"
          color="var(--gray-300)"
          width={!dates?.date ? '220px' : undefined}
          maxWidth="100%"
          height={!dates?.date ? '16px' : undefined}
          backgroundColor={!dates.date ? 'rgba(var(--gray-800-rgb), 0.7)' : undefined}
        >
          {dates?.date?.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone,
          }) || emptySpace}
          <OptionalComponent display={!!dates.endDate}>
            <>
              {' - '}
              {dates.endDate?.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone,
              })}{' '}
              • Runtime: {getRuntime()}
            </>
          </OptionalComponent>
        </Text>
      </div>
    </TooltipPortal>
  );
};

export default DateDescription;
